import React, {useEffect, useMemo, useState} from 'react';
import {Badge, Button, Space, Tabs, message} from 'antd';
import {DownloadOutlined, SettingOutlined, PlusOutlined} from '@ant-design/icons';
import dayjs from 'dayjs';
import {useNavigate} from 'react-router-dom';
import {useAppSelector} from '../../hooks/hooks';
import {useEventsData, EventFilterState, defaultEventFilters} from '../../hooks/events/useEventsData';
import {useEventsStats} from '../../hooks/events/useEventsStats';
import EventGrid from './grid/EventGrid';
import EventsKpiBar from './components/EventsKpiBar';
import EventsFilterBar, {FilterChip, GroupField, SortDir, SortField} from './components/EventsFilterBar';
import PageContainer from '../../components/common/PageContainer';
import PrimaryButton from '../../components/common/PrimaryButton';
import {useTranslation} from '../../hooks/useTranslation';
import {useSetBreadcrumb} from '../../contexts/BreadcrumbContext';
import {PAGINATION} from '../../constants/constants';
import {PaginationState} from '../../types';
import {LossEvent} from '../../types/events';
import './events.less';

const severityKeyMap: Record<string, string> = {
  TIER_1: 'critical', TIER_2: 'high', TIER_3: 'medium', TIER_4: 'low',
};

const Events: React.FC = () => {
  const isAuthenticated = useAppSelector(state => state.session.isAuthenticated);
  const {t} = useTranslation();
  const navigate = useNavigate();

  useSetBreadcrumb([
    {label: t('navigation.workspace'), to: '/'},
    {label: t('navigation.appName'), to: '/events'},
    {label: t('navigation.appSwitcherModules.lossEvents')},
  ]);

  const [filters, setFilters] = useState<EventFilterState>(defaultEventFilters);
  const [pagination, setPagination] = useState<PaginationState>({
    current: PAGINATION.DEFAULT_PAGE,
    pageSize: PAGINATION.DEFAULT_PAGE_SIZE,
    total: 0,
  });
  const [activeTab, setActiveTab] = useState<string>('all');
  const [hasYtd, setHasYtd] = useState(false);
  const [groupBy, setGroupBy] = useState<GroupField>('lossEventType');
  const [sortField, setSortField] = useState<SortField>('occurrenceDate');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  const {
    searchResults,
    totalCount,
    filterOptions,
    isSearchLoading,
    isSearchError,
    searchError,
  } = useEventsData(filters, pagination, isAuthenticated);

  const {data: stats, isLoading: isStatsLoading} = useEventsStats(isAuthenticated);

  useEffect(() => {
    if (isSearchError && searchError) {
      message.error(t('events.messages.loadError'));
    }
  }, [isSearchError, searchError, t]);

  const handleFilterChange = (key: keyof EventFilterState, value: any) => {
    setFilters(prev => ({...prev, [key]: value, hasFilters: true}));
    setPagination(prev => ({...prev, current: 1}));
  };

  const handleRemoveChip = (key: string) => {
    setFilters(prev => ({...prev, [key]: undefined, hasFilters: true}));
    setPagination(prev => ({...prev, current: 1}));
  };

  const handleToggleYtd = () => {
    const next = !hasYtd;
    setHasYtd(next);
    if (next) {
      const from = dayjs().startOf('year').format('YYYY-MM-DD');
      const to = dayjs().format('YYYY-MM-DD');
      handleFilterChange('dateRange', [from, to]);
    } else {
      handleFilterChange('dateRange', null);
    }
  };

  const handleSortChange = (field: SortField, dir: SortDir) => {
    setSortField(field);
    setSortDir(dir);
  };

  const clearAllFilters = () => {
    setFilters(defaultEventFilters);
    setHasYtd(false);
    setPagination(prev => ({...prev, current: 1}));
  };

  const handleTabChange = (key: string) => {
    setActiveTab(key);
    setPagination(prev => ({...prev, current: 1}));
  };

  const handleTableChange = (paginationConfig: any) => {
    setPagination({
      current: paginationConfig.current,
      pageSize: paginationConfig.pageSize,
      total: totalCount,
    });
  };

  const filterRowsByTab = (rows: LossEvent[], tab: string): LossEvent[] => {
    if (tab === 'all') return rows;
    if (tab === 'open') return rows.filter(r => r.status?.code && !/clos|cerr|reconcil|recov/i.test(r.status.code));
    if (tab === 'investigating') return rows.filter(r => /regist|invest/i.test(r.status?.code || ''));
    if (tab === 'recovered') return rows.filter(r => /reconcil|recov/i.test(r.status?.code || ''));
    if (tab === 'closed') return rows.filter(r => /clos|cerr/i.test(r.status?.code || ''));
    return rows;
  };

  const tabFilteredResults = useMemo(
    () => filterRowsByTab(searchResults, activeTab),
    [searchResults, activeTab]
  );

  const sortedResults = useMemo(() => {
    const arr = [...tabFilteredResults];
    const mul = sortDir === 'asc' ? 1 : -1;
    arr.sort((a, b) => {
      const av = (a as any)[sortField] ?? '';
      const bv = (b as any)[sortField] ?? '';
      if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * mul;
      return String(av).localeCompare(String(bv)) * mul;
    });
    return arr;
  }, [tabFilteredResults, sortField, sortDir]);

  const tabCounts = useMemo(() => ({
    all: totalCount || searchResults.length,
    open: filterRowsByTab(searchResults, 'open').length,
    investigating: filterRowsByTab(searchResults, 'investigating').length,
    recovered: filterRowsByTab(searchResults, 'recovered').length,
    closed: filterRowsByTab(searchResults, 'closed').length,
  }), [searchResults, totalCount]);

  // Build chips from active filters
  const filterChips = useMemo((): FilterChip[] => {
    const chips: FilterChip[] = [];
    const getLabel = (field: string, code: string): string => {
      const opts: any[] = (filterOptions as any)?.[field] || [];
      const found = opts.find((o: any) => (o.code || o.id || String(o)) === code);
      return found?.name || found?.description || code;
    };
    if (filters.status) chips.push({key: 'status', label: getLabel('status', filters.status)});
    if (filters.severityTier) {
      const k = severityKeyMap[filters.severityTier];
      chips.push({key: 'severityTier', label: k ? t(`events.severity.${k}`) : filters.severityTier});
    }
    if (filters.lossEventType) chips.push({key: 'lossEventType', label: getLabel('lossEventType', filters.lossEventType)});
    if (filters.businessLine) chips.push({key: 'businessLine', label: getLabel('businessLine', filters.businessLine)});
    return chips;
  }, [filters, filterOptions, t]);

  const tabItems = (['all', 'open', 'investigating', 'recovered', 'closed'] as const).map(key => ({
    key,
    label: (
      <Space size={6}>
        {t(`events.tabs.${key}`)}
        <Badge
          count={tabCounts[key]}
          showZero
          overflowCount={9999}
          color={activeTab === key ? undefined : '#bcc4d3'}
          style={{
            backgroundColor: activeTab === key ? '#ECF8FF' : undefined,
            borderColor: activeTab === key ? '#91CAFF' : undefined,
            color: activeTab === key ? '#0769D9' : undefined,
            fontWeight: activeTab === key ? 'bold' : undefined,
          }}
        />
      </Space>
    ),
  }));

  if (!isAuthenticated) {
    return (
      <div style={{display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400}}>
        <div>{t('common.authRequired')}</div>
      </div>
    );
  }

  return (
    <PageContainer
      title={t('events.title')}
      subTitle={t('events.subtitle')}
      className="events-container"
      bordered={false}
      headerPaddingBottom={true}
      extra={
        <Space>
          <Button icon={<DownloadOutlined />}>{t('events.export')}</Button>
          <Button icon={<SettingOutlined />}>{t('events.customise')}</Button>
          <PrimaryButton type="primary" icon={<PlusOutlined />} onClick={() => navigate('/events/new')}>
            {t('events.createEvent')}
          </PrimaryButton>
        </Space>
      }
    >
      <EventsKpiBar stats={stats} loading={isStatsLoading} />

      <Tabs
        activeKey={activeTab}
        onChange={handleTabChange}
        items={tabItems}
        className="gt-event-tabs"
        tabBarStyle={{margin: 0}}
      />

      <EventsFilterBar
        chips={filterChips}
        onRemoveChip={handleRemoveChip}
        hasYtd={hasYtd}
        onToggleYtd={handleToggleYtd}
        groupBy={groupBy}
        onGroupByChange={setGroupBy}
        sortField={sortField}
        sortDir={sortDir}
        onSortChange={handleSortChange}
        onExport={() => {}}
      />

      <EventGrid
        dataSource={sortedResults}
        loading={isSearchLoading}
        onTableChange={handleTableChange}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: activeTab === 'all' ? totalCount : tabFilteredResults.length,
        }}
      />
    </PageContainer>
  );
};

export default Events;
